import {jsx, ParamComponent} from 'panel';
import {selx} from 'clsx';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {DashboardThumbnailContents, MediaThumbnailInfo, ReportThumbnailInfo} from 'common/report/util/search/types';
import {
  ContentType,
  LayoutCell,
  LayoutResponse,
  LayoutRowResponse,
  MediaCardType,
} from 'common/report/dashboards/types';
import {getOrderedGridRows} from 'common/report/util/dashboards/grid-layout';
import {getMediaCardImageSrc} from 'common/report/dashboards/media-card';

import {getEntityImage} from '../util';
import TEXT_CARD_IMAGE from '../images/text-card.svg';
import VIDEO_CARD_IMAGE from '../images/video-card.svg';
import css from './index.module.styl?inline';
import styles from './index.module.styl';

type DashboardThumbnailParams = {
  layout: LayoutResponse;
  contents: DashboardThumbnailContents;
};

type ThumbnailInfo = MediaThumbnailInfo | ReportThumbnailInfo | undefined;

function isMediaThumbnailInfo(thumbnailInfo: ThumbnailInfo): thumbnailInfo is MediaThumbnailInfo {
  return (thumbnailInfo as MediaThumbnailInfo)?.media_type !== undefined;
}

function isReportThumbnailInfo(thumbnailInfo: ThumbnailInfo): thumbnailInfo is ReportThumbnailInfo {
  return (thumbnailInfo as ReportThumbnailInfo)?.chart_type !== undefined;
}

function getImageForCell(cell: LayoutCell, thumbnailInfo: ThumbnailInfo) {
  if (cell.content_type === ContentType.Text) {
    return TEXT_CARD_IMAGE;
  }

  if (cell.content_type === ContentType.Media && isMediaThumbnailInfo(thumbnailInfo)) {
    if (thumbnailInfo.media_type === MediaCardType.Image) {
      return getMediaCardImageSrc(thumbnailInfo.path);
    } else {
      return VIDEO_CARD_IMAGE;
    }
  }

  if (
    [ContentType.Report, ContentType.ReportLink].includes(cell.content_type) &&
    isReportThumbnailInfo(thumbnailInfo)
  ) {
    return getEntityImage(thumbnailInfo.chart_type, thumbnailInfo.bookmark_type);
  }

  return getEntityImage();
}

function GridRowThumbnail({row, contents}: {row: LayoutRowResponse; contents: DashboardThumbnailContents}) {
  let columnStart = 1;
  return (
    <>
      {row.cells.map((cell) => {
        const gridColumn = `${columnStart} / ${columnStart + cell.width}`;
        columnStart += cell.width;

        const thumbnailInfo = contents[cell.content_type]?.[cell.content_id];
        return (
          <div sel={selx(styles.dashboardCard)} style={{'grid-column': gridColumn}}>
            <img
              sel={selx(styles.dashboardCardBookmarkImage)}
              class={{[styles.isMediaCard]: cell.content_type === `media`}}
              attrs={{src: getImageForCell(cell, thumbnailInfo), loading: `lazy`}}
            />
          </div>
        );
      })}
    </>
  );
}

function getGridHeightCss(layout: LayoutResponse) {
  const numRows = layout.order.length;
  if (numRows > 1) {
    // overflow the thumbnail so that last row is half cut-off per design.
    // this will probably have to be tweaked when we want to use larger thumbnails.
    const heightPercentageWithOverflow = 100 + 100 / numRows;
    const gapOffset = numRows * 8;
    return `calc(${heightPercentageWithOverflow}% - ${gapOffset}px)`;
  }

  return `100%`;
}

export class DashboardThumbnail extends ParamComponent<DashboardThumbnailParams> {
  get config() {
    return {
      css,
      params: {
        layout: Object,
        contents: Object,
      },
      useShadowDom: true,
      template: () => {
        const heightCss = getGridHeightCss(this.params.layout);
        const layoutRows = getOrderedGridRows(this.params.layout);

        return (
          <div sel={selx(styles.dashboardThumbnail)}>
            <div sel={selx(styles.dashboardGrid)} style={{'min-height': heightCss}}>
              {layoutRows.map((row) => {
                return <GridRowThumbnail row={row} contents={this.params.contents} />;
              })}
            </div>
          </div>
        );
      },
    };
  }
}

export const DashboardThumbnailEl = defineAndMakeJsxFactory(`mp-dashboard-thumbnail`, DashboardThumbnail);
export const DashboardThumbnailReact = panel2react(`mp-dashboard-thumbnail`, DashboardThumbnail);
